import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';

const logger = LogManager.getLogger('hook:useStorySpeakers');

/**
 * Loads Dato speakers — both story-level AND organisation-level (showcase) —
 * for the given story. Used by the face-mapping UI to render the speaker
 * dropdown and to gate buttons that require at least one speaker to have
 * a headshot. The actual headshotUrls payload sent to the
 * face-identification pipeline is resolved server-side from storyId +
 * showcaseId, so this hook intentionally does not expose it.
 */
export function useStorySpeakers(storyId: string | undefined) {
  const speakerService = useSpeakerManagementService();

  const [speakers, setSpeakers] = useState<SpeakerWithColor[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!storyId) {
      setSpeakers([]);
      return;
    }
    setLoading(true);
    try {
      const result = await speakerService.fetchSpeakersForStory(storyId);
      if (!result.ok) {
        logger.error('fetchSpeakersForStory failed', {
          code: result.error.code,
          message: result.error.message,
        });
        setSpeakers([]);
        return;
      }
      // Merge story + org by speaker id (story wins on duplicates).
      const merged: SpeakerWithColor[] = [];
      const seen = new Set<string>();
      for (const s of [
        ...result.value.storySpeakers,
        ...result.value.orgSpeakers,
      ]) {
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        merged.push(s);
      }
      setSpeakers(merged);
    } catch (error) {
      logger.error('Failed to fetch speakers', extractErrorInfo(error));
      setSpeakers([]);
    } finally {
      setLoading(false);
    }
  }, [storyId, speakerService]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Client-side hint: does at least one fetched speaker have a non-empty
   * headshot? Used to disable re-run buttons with a useful tooltip before
   * the request hits the server.
   */
  const hasSpeakerWithHeadshot = useMemo(
    () =>
      speakers.some(s =>
        (s.headshots ?? []).some(
          h => typeof h?.url === 'string' && h.url.length > 0,
        ),
      ),
    [speakers],
  );

  return { speakers, hasSpeakerWithHeadshot, loading, refresh: load };
}
